/* eslint-disable react/prop-types */
import React, { useMemo, useState } from "react";
import {
  Card,
  Col,
  Divider,
  Layout,
  Row,
  Select,
  Statistic,
  Typography,
} from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import functions from "../../components/utils/functions";

const { Title } = Typography;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const chartAxisColor = "#a3a3a3";
const chartGridColor = "#262626";

/** Fills vertical space and zooms Y to data range so spikes read clearly (not squashed to 0–max). */
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "top",
      labels: { color: chartAxisColor },
    },
    title: { display: false },
  },
  elements: {
    line: {
      tension: 0.12,
      borderWidth: 2.5,
    },
    point: {
      radius: 4,
      hoverRadius: 7,
      borderWidth: 2,
      hitRadius: 12,
    },
  },
  scales: {
    x: {
      ticks: {
        color: chartAxisColor,
        autoSkip: true,
        maxRotation: 45,
        minRotation: 0,
      },
      grid: { color: chartGridColor },
    },
    y: {
      beginAtZero: false,
      grace: "12%",
      ticks: { color: chartAxisColor },
      grid: { color: chartGridColor },
    },
  },
};

const chartWrapStyle = {
  position: "relative",
  width: "100%",
  height: 340,
  maxWidth: "100%",
};

const MONTH_RANGE_OPTIONS = [
  { value: "6", label: "Last 6 months" },
  { value: "12", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

function sliceRange(labels, platforms, rangeKey) {
  const n = rangeKey === "all" ? labels.length : rangeKey === "6" ? 6 : 12;
  const start = Math.max(0, labels.length - n);
  const L = labels.slice(start);
  const P = platforms.map((p) => ({
    ...p,
    views: p.views.slice(start),
    users: p.users.slice(start),
    timeMin: p.timeMin.slice(start),
    revenue: p.revenue.slice(start),
  }));
  return { labels: L, platforms: P };
}

function filterPlatform(platforms, platformId) {
  if (!platformId || platformId === "all") return platforms;
  return platforms.filter((p) => p.id === platformId);
}

function sum(arr) {
  return arr.reduce((a, b) => a + (Number(b) || 0), 0);
}

function withSpanGaps(datasets) {
  return datasets.map((ds) => ({ ...ds, spanGaps: true }));
}

/**
 * Demo-only streaming charts — static series, no API.
 */
const DemoMetricCharts = ({ chart }) => {
  const [selectedMonthRange, setSelectedMonthRange] = useState("6");
  const [selectedPlatformId, setSelectedPlatformId] = useState("all");

  const { labels, platforms } = useMemo(
    () => sliceRange(chart.labels, chart.platforms, selectedMonthRange),
    [chart, selectedMonthRange]
  );

  const activePlatforms = useMemo(
    () => filterPlatform(platforms, selectedPlatformId),
    [platforms, selectedPlatformId]
  );

  const platformOptions = useMemo(
    () => [
      { value: "all", label: "All platforms" },
      ...chart.platforms.map((p) => ({ value: p.id, label: p.title })),
    ],
    [chart.platforms]
  );

  const hasRevenue = activePlatforms.some((p) =>
    p.revenue.some((v) => v != null && Number(v) > 0)
  );

  const sumStreams = sum(activePlatforms.flatMap((p) => p.views));
  const sumUsers = sum(activePlatforms.flatMap((p) => p.users));
  const sumTime = sum(activePlatforms.flatMap((p) => p.timeMin));
  const sumRevenue = sum(activePlatforms.flatMap((p) => p.revenue));

  const revData = {
    labels,
    datasets: withSpanGaps(
      activePlatforms.map((p) => ({
        label: p.title,
        data: p.revenue,
        borderColor: p.color,
        backgroundColor: p.color,
        fill: false,
      }))
    ),
  };

  const metricDefs = [
    { key: "views", title: "Total Streams" },
    { key: "users", title: "Total Streaming Users" },
    { key: "timeMin", title: "Total Stream Duration (min)" },
  ];

  const screenWidth = typeof window !== "undefined" ? window.innerWidth : 1024;

  const StatisticsCard = ({ title, value }) => (
    <Col
      xs={{ span: hasRevenue ? 12 : 8 }}
      lg={{ span: hasRevenue ? 6 : 8 }}
      style={{
        display: screenWidth > 768 ? "block" : "flex",
        marginBottom: 16,
      }}
    >
      <Card style={{ width: "100%", height: "100%" }}>
        <Statistic title={title} value={value} />
      </Card>
    </Col>
  );

  return (
    <>
      <Divider />
      <Row gutter={[16, 16]} align="middle">
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Title level={4} style={{ margin: 0 }}>
            Streaming Analytics
          </Title>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Row gutter={16} align="middle">
            <Col flex="none">
              <Select
                value={selectedMonthRange}
                onChange={setSelectedMonthRange}
                options={MONTH_RANGE_OPTIONS}
                style={{ minWidth: 160 }}
              />
            </Col>
            <Col flex="none">
              <Select
                value={selectedPlatformId}
                onChange={setSelectedPlatformId}
                options={platformOptions}
                style={{ minWidth: 180 }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Layout className="vc-panel" style={{ padding: 8 }}>
        <Row gutter={16} style={{ padding: 8 }}>
          {hasRevenue && (
            <StatisticsCard
              title="Net Revenue"
              value={`₹ ${functions.formatIndianNumber(sumRevenue, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            />
          )}
          <StatisticsCard
            title="Total Streams"
            value={functions.formatIndianNumber(sumStreams)}
          />
          <StatisticsCard
            title="Total Streaming Users"
            value={functions.formatIndianNumber(sumUsers)}
          />
          <StatisticsCard
            title="Total Stream Duration (min)"
            value={functions.formatIndianNumber(sumTime)}
          />
        </Row>
        {hasRevenue && (
          <>
            <Title level={5}>Revenue (INR)</Title>
            <div style={chartWrapStyle}>
              <Line options={chartOptions} data={revData} />
            </div>
          </>
        )}
        {metricDefs.map(({ key, title }) => (
          <React.Fragment key={key}>
            <Title level={5} style={{ marginTop: 48 }}>
              {title}
            </Title>
            <div style={chartWrapStyle}>
              <Line
                options={chartOptions}
                data={{
                  labels,
                  datasets: withSpanGaps(
                    activePlatforms.map((p) => ({
                      label: p.title,
                      data: p[key],
                      borderColor: p.color,
                      backgroundColor: p.color,
                      fill: false,
                    }))
                  ),
                }}
              />
            </div>
          </React.Fragment>
        ))}
      </Layout>
    </>
  );
};

export default DemoMetricCharts;
